// Fiches de lecture de la Bibliothèque (13/10) : #/ressource?id=… (vidéo, PDF) et #/ressource-lecture?id=… (enveloppe PDF).
// Catalogue UNIQUE (bibliotheque-fixtures RESOURCES), aucune copie ; l'article garde sa feuille existante dans la Bibliothèque.
// Vidéo : le lecteur existant DemoVideo (iframe YouTube officielle, contrôles YouTube, plein écran) avec une source QUALIFIÉE de
// video-fixtures, seulement quand la ressource montre le même mouvement (« Technique du squat gobelet » → « Squat gobelet ») :
// démonstration EXTERNE, jamais présentée comme un contenu ACS. Sans source qualifiée : « Vidéo de démonstration à venir »
// (texte existant de la Séance). PDF : aucun fichier PDF n'existe dans le prototype, donc pas d'aperçu de première page ni de
// téléchargement de ce fichier ; l'aperçu texte existant (body) reste. La lecture (« Lire le document ») ouvre le lecteur natif
// générique (pdf-reader.tsx, Mozilla PDF.js) sur l'exemple officiel PDF.js, présenté comme document de démonstration.
import { ChevronLeft, FileText, Video } from "lucide-react";
import { Button } from "@ubm/ui/components/button";
import { StatusBar, type Route } from "./parts";
import { ShellActions } from "./shell";
import { useShellImmersive } from "./shell-state";
import { DemoVideo } from "./video-player";
import { exerciseVideo } from "./video-fixtures";
import { KIND_LABEL, RESOURCES, type Resource } from "./bibliotheque-fixtures";
import { COACHES, messagerie } from "./messagerie-state";
import { useState, useSyncExternalStore } from "react";
import { PdfPreview, PdfReader } from "./pdf-reader";
import helloPdf from "../assets/pdf/helloworld.pdf";

interface PageProps { go: (route: Route) => void; onOutOfScope: (label: string) => void }

/** Qualified external demonstration for a resource showing the SAME movement as an exercise source (never another clip). */
const RESOURCE_VIDEO: Record<string, string> = { r2: "Squat gobelet" };

/** No PDF file exists for the catalogue's fiches: every one opens the official PDF.js « Hello, world! » example
 *  (assets/pdf/README.md), named as such, never presented as an ACS document. */
// The file loader emits a path relative to the BUNDLE (dist/app.js), not to the page: resolve it against the module URL.
const DEMO_PDF = { url: new URL(helloPdf as string, import.meta.url).href, title: "Document de démonstration PDF.js", missing: "Le fichier de cette fiche n’existe pas encore." };
/** Page read per resource, kept for this session only (memory, not stored). */
const readPositions = new Map<string, number>();
/** main.tsx only re-renders on a ROUTE change; #/ressource?id=r1 → ?id=r2 keeps the same route. The resource screens subscribe to
 *  the hash themselves so the id (and via) are read again on every hash change, without touching the other routes. */
const subscribeHash = (on: () => void) => { window.addEventListener("hashchange", on); return () => window.removeEventListener("hashchange", on); };
const useHash = () => useSyncExternalStore(subscribeHash, () => window.location.hash, () => "");
const param = (name: string) => window.location.hash.match(new RegExp(`[?&]${name}=([^&]+)`))?.[1];
const resourceOf = (): Resource | undefined => RESOURCES.find(r => r.id === param("id"));
/** Back target: the Bibliothèque, opened from the studio or not (exact previous context). */
const libraryHash = () => param("via") === "studio" ? "#/programme?retour=studio" : "#/programme";
const ficheHash = (r: Resource) => `#/ressource?id=${r.id}${param("via") ? `&via=${param("via")}` : ""}`;

function Missing() {
  return <div className="cc-screen cc-ressource"><StatusBar /><div className="cc-scroll">
    <header className="cc-account-head">
      <button type="button" className="cc-icon-btn" aria-label="Retour à la bibliothèque" onClick={() => { window.location.hash = libraryHash(); }}><ChevronLeft size={18} strokeWidth={2} /></button>
      <h1 className="cc-title">Ressource</h1><ShellActions />
    </header>
    <p className="cc-biblio-empty">Cette ressource n'est plus disponible.</p>
  </div></div>;
}

/** #/ressource?id=… — fiche vidéo ou PDF (dock visible, en-tête standard). */
export function Ressource(_: PageProps) {
  useHash();
  const r = resourceOf();
  // Real page count of the PDF previewed on the fiche (hook kept before any early return).
  const [pages, setPages] = useState<number | null>(null);
  if (!r || r.kind === "article") return <Missing />;
  const video = r.kind === "video" && RESOURCE_VIDEO[r.id] ? exerciseVideo(RESOURCE_VIDEO[r.id]!) : undefined;
  const coach = COACHES[0];
  const Icon = r.kind === "video" ? Video : FileText;
  return <div className="cc-screen cc-ressource">
    <StatusBar />
    <div className="cc-scroll">
      <header className="cc-account-head">
        <button type="button" className="cc-icon-btn" aria-label="Retour à la bibliothèque" onClick={() => { window.location.hash = libraryHash(); }}><ChevronLeft size={18} strokeWidth={2} /></button>
        <p className="cc-title cc-ressource-short">{KIND_LABEL[r.kind]}</p>
        <ShellActions />
      </header>

      {r.kind === "video"
        ? video
          ? <div className="cc-ressource-media"><DemoVideo video={video} label={`Démonstration : ${r.title}`} />
              {/* The linked source credit is shown by DemoVideo itself; only the clip's own note is added here. */}
              {video.provider === "youtube" && video.note && <p className="cc-ressource-credit">{video.note}</p>}</div>
          : <div className="cc-seance-video cc-ressource-media" role="note"><Video size={18} strokeWidth={1.8} /><span><b>Vidéo de démonstration à venir</b></span></div>
        : <figure className="cc-ressource-media cc-ressource-pdf">
            {/* First page of the real demo file, painted by the same engine as the reader; page count read from the document. */}
            <PdfPreview url={DEMO_PDF.url} title={DEMO_PDF.title} onLoad={setPages} />
            <figcaption className="cc-ressource-credit">{DEMO_PDF.title}{pages !== null ? ` · ${pages} page${pages > 1 ? "s" : ""}` : ""}. {DEMO_PDF.missing}</figcaption>
          </figure>}

      <h1 className="cc-ressource-title">{r.title}</h1>
      <p className="cc-biblio-format"><Icon size={13} strokeWidth={2} /> {KIND_LABEL[r.kind]}{r.duration ? ` · ${r.duration}` : ""}</p>
      {r.intro && <p className="cc-ressource-intro">{r.intro}</p>}

      {r.body?.length ? <section className="cc-ressource-block" aria-labelledby="res-consignes">
        <h2 id="res-consignes" className="cc-ressource-h2">Consignes</h2>
        <ol className="cc-ressource-list">{r.body.map(x => <li key={x}>{x}</li>)}</ol>
      </section> : null}

      {r.kind === "pdf" && <Button variant="primary" size="lg" className="cc-ressource-cta" onClick={() => { window.location.hash = `#/ressource-lecture?id=${r.id}${param("via") ? `&via=${param("via")}` : ""}`; }}>Lire le document</Button>}

      {coach && <Button variant="ghost" size="sm" className="cc-ressource-ask" onClick={() => { messagerie.startWithContext(coach.id, { label: `À propos de ${r.title}`, href: ficheHash(r) }); window.location.hash = `#/messages?fil=${coach.id}`; }}>Poser une question à {coach.name}</Button>}
      <p className="cc-home-demo">Prototype : contenu de démonstration partagé par ton coach.</p>
    </div>
  </div>;
}

/** #/ressource-lecture?id=… — lecture PDF dans le lecteur natif générique (pdf-reader.tsx), vue focalisée (pas de dock ni
 *  d'actions d'en-tête). Le document est l'exemple officiel PDF.js tant que le fichier de la fiche n'existe pas ; la position
 *  lue est gardée pour cette session seulement, par ressource (aucune persistance). */
export function RessourceLecture(_: PageProps) {
  useShellImmersive(true);
  useHash();
  const r = resourceOf();
  const back = () => { window.location.hash = r ? ficheHash(r) : libraryHash(); };
  const key = r?.id ?? "?";
  return <div className="cc-screen cc-ressource cc-ressource-reader">
    <StatusBar />
    <PdfReader key={key} url={DEMO_PDF.url} title={DEMO_PDF.title} onBack={back} downloadUrl={DEMO_PDF.url} downloadName="helloworld.pdf"
      initialPage={readPositions.get(key)} onPosition={pg => readPositions.set(key, pg)} />
  </div>;
}
